import React, {useState, useEffect, ReactNode} from 'react';
import {ReactableColumnDef, InfoBox, Reactable} from '@deviceinsight/ng-ui-components';

import {Pageable, PaginationSettings} from '@deviceinsight/ng-ui-api-client';

import warning_icon from '../../../assets/icon_warning_triangle.svg';
import {DataProcessingDefinitionValidationError} from '../types';
import './ErrorInfoTable.scss';
import {getDataProcessingDefinitionById} from '../../../api/dataProcessingDefinitionApi';

export type ValidationErrorTableItem = {
	id: number;
	assetGroupName?: string;
	assetName: string;
	calculatedDPName: string;
};
type Props = {
	currentDpdValidationErrors: Array<DataProcessingDefinitionValidationError>;
	kpiName?: string;
	warningTitle: string;
	warningDetail: ReactNode | null | undefined;
	assetGroupName?: string;
	columnDefs: Array<ReactableColumnDef<ValidationErrorTableItem>>;
};

const ErrorInfoTable = ({
	currentDpdValidationErrors,
	kpiName,
	warningTitle,
	warningDetail,
	assetGroupName,
	columnDefs
}: Props) => {
	const calculatedDPName = kpiName ? kpiName : null;
	const initialPaginationSettings: PaginationSettings = {
		page: 0,
		size: 10,
		sort: {
			property: assetGroupName ? 'assetGroupName' : 'assetName',
			direction: 'ASC'
		}
	};
	const [paginationSettings, setPaginationSettings] = useState<PaginationSettings>(initialPaginationSettings);
	const [validationErrors, setValidationErrors] = useState<Array<ValidationErrorTableItem>>([]);
	const pageable: Pageable<ValidationErrorTableItem> = {
		content: validationErrors,
		totalElements: validationErrors.length,
		totalElementsAccurate: validationErrors.length < 10000,
		totalPages: 1,
		number: 0,
		size: validationErrors.length,
		sort: [
			{
				property: assetGroupName ? 'assetGroupName' : 'assetName',
				direction: 'ASC'
			}
		],
		first: true,
		last: true
	};

	const onChangePage = (page: number) => {
		setPaginationSettings({...paginationSettings, page});
	};

	const onSort = (property: string, direction: 'ASC' | 'DESC') => {
		setPaginationSettings({
			...paginationSettings,
			sort: {
				property,
				direction
			}
		});
	};

	const getCalculatedDPName = async (dpdId: number) => {
		const {
			definition: {kpiName: calculatedDPName}
		} = await getDataProcessingDefinitionById(dpdId);
		return calculatedDPName;
	};

	useEffect(() => {
		const getValidationErrorTableItem = async (
			validationError: DataProcessingDefinitionValidationError,
			index: number
		): Promise<ValidationErrorTableItem> => {
			const validCalculatedDPName =
				calculatedDPName || (await getCalculatedDPName(validationError.dataProcessingDefinitionId));
			return {
				id: index,
				assetGroupName: assetGroupName,
				assetName: validationError.assetName,
				calculatedDPName: validCalculatedDPName
			};
		};

		const getValidationErrorTableItems = async () => {
			const newValidationErrors = await Promise.all(currentDpdValidationErrors.map(getValidationErrorTableItem));
			setValidationErrors(newValidationErrors);
		};

		getValidationErrorTableItems();
	}, [currentDpdValidationErrors, assetGroupName, calculatedDPName]);
	return (
		<InfoBox className="errorInfoTable" type="error" icon={warning_icon} message={warningTitle}>
			{<div className="errorInfoTable-detail">{warningDetail}</div>}
			<div className="errorInfoTable-detail-table">
				<Reactable
					columnDefs={columnDefs}
					isLoading={false}
					pageable={pageable}
					uniqueItemKey={(item) => item.id}
					paginationSettings={paginationSettings}
					onChangePage={onChangePage}
					onSort={onSort}
				/>
			</div>
		</InfoBox>
	);
};

export default ErrorInfoTable;
